import { api } from "@stoneflow/backend/convex/_generated/api"
import { createFileRoute } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { Plus } from "lucide-react"
import { useEffect, useState } from "react"
import { AppLink } from "#/components/app-link"
import { LogRoleGate } from "#/components/capability-gate"
import { DonutChart } from "#/components/donut-chart"
import { RouteError } from "#/components/route-error"
import { DashboardSkeleton } from "#/components/skeletons/dashboard-skeleton"
import { StatsCard } from "#/components/stats-card"
import { Button, buttonVariants } from "#/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "#/components/ui/card"
import { useAppNavigate } from "#/hooks/use-app-navigate"
import { useStonelog } from "#/lib/stonelog-context"

export const Route = createFileRoute("/_authenticated/stonelog/dashboard")({
	errorComponent: RouteError,
	component: Dashboard,
})

const TIME_FILTERS = [
	{ label: "Last 7 days", days: 7 },
	{ label: "Last 30 days", days: 30 },
	{ label: "Last 90 days", days: 90 },
	{ label: "All time", days: undefined },
] as const

function Dashboard() {
	const { currentLogId, currentLog } = useStonelog()
	const [days, setDays] = useState<number | undefined>(30)
	const navigate = useAppNavigate()

	const stats = useQuery(
		api.stonelog.stats.dashboard,
		currentLogId ? { logId: currentLogId, days } : "skip",
	)

	useEffect(() => {
		if (!currentLogId) navigate({ to: "/stonelog/select-log" })
	}, [currentLogId, navigate])

	if (!currentLogId) return null

	if (stats === undefined) {
		return <DashboardSkeleton />
	}

	return (
		<div className="mx-auto max-w-6xl space-y-6 p-6">
			{/* Header */}
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="min-w-0">
					<h1 className="mb-1">Dashboard</h1>
					<p className="text-sm text-foreground-secondary">
						{currentLog?.name
							? `${currentLog.name} Operations Exceptions Log`
							: "Operations Exceptions Log"}
					</p>
				</div>
				<LogRoleGate logId={currentLogId} minRole="editor">
					<AppLink
						to={"/stonelog/exceptions/new" as string}
						className={buttonVariants({
							variant: "default",
							className: "gap-2 w-full sm:w-auto",
						})}
					>
						<Plus className="size-4" />
						New Exception
					</AppLink>
				</LogRoleGate>
			</div>

			{/* Time filter */}
			<div className="flex items-center gap-2">
				{TIME_FILTERS.map((f) => (
					<Button
						key={f.label}
						variant="ghost"
						size="sm"
						onClick={() => setDays(f.days)}
						className={days === f.days ? "bg-accent font-medium" : ""}
					>
						{f.label}
					</Button>
				))}
			</div>

			{/* Status cards */}
			<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
				<StatsCard label="New" value={stats?.statusCounts.new ?? "—"} borderColor="var(--info)" />
				<StatsCard
					label="Open"
					value={stats?.statusCounts.open ?? "—"}
					borderColor="var(--warning)"
				/>
				<StatsCard
					label="In Progress"
					value={stats?.statusCounts.in_progress ?? "—"}
					borderColor="var(--chart-3)"
				/>
				<StatsCard
					label="Resolved"
					value={stats?.statusCounts.resolved ?? "—"}
					borderColor="var(--success)"
				/>
				<StatsCard
					label="Closed"
					value={stats?.statusCounts.closed ?? "—"}
					borderColor="var(--chart-4)"
				/>
			</div>

			{/* Average open age */}
			<StatsCard
				label="Avg. Open Age"
				value={stats ? `${Math.round(stats.averageOpenAge)}d` : "—"}
			/>

			{/* Charts */}
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<Card>
					<CardHeader className="pb-2">
						<CardTitle>Exceptions by Type</CardTitle>
					</CardHeader>
					<CardContent>
						<DonutChart
							data={(stats?.typeCounts ?? []).map((t: { typeName: string; count: number }) => ({
								name: t.typeName,
								value: t.count,
							}))}
							centerLabel={
								stats
									? String(
											stats.typeCounts.reduce((s: number, t: { count: number }) => s + t.count, 0),
										)
									: undefined
							}
						/>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="pb-2">
						<CardTitle>Exceptions by Department</CardTitle>
					</CardHeader>
					<CardContent>
						<DonutChart
							data={(stats?.raisingDepartmentCounts ?? []).map(
								(d: { departmentName: string; count: number }) => ({
									name: d.departmentName,
									value: d.count,
								}),
							)}
							centerLabel={
								stats
									? String(
											stats.raisingDepartmentCounts.reduce(
												(s: number, d: { count: number }) => s + d.count,
												0,
											),
										)
									: undefined
							}
						/>
					</CardContent>
				</Card>
			</div>

			{/* Quick actions */}
			<Card>
				<CardHeader className="pb-2">
					<CardTitle>Quick Actions</CardTitle>
				</CardHeader>
				<CardContent className="flex gap-4">
					<LogRoleGate logId={currentLogId} minRole="editor">
						<AppLink
							to={"/stonelog/exceptions/new" as string}
							className={buttonVariants({ variant: "secondary" })}
						>
							<Plus className="size-4" />
							Create Exception
						</AppLink>
					</LogRoleGate>
					<AppLink
						to={"/stonelog/exceptions" as string}
						className={buttonVariants({ variant: "secondary" })}
					>
						View All Exceptions
					</AppLink>
				</CardContent>
			</Card>
		</div>
	)
}
